import { useState, useEffect, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { entrevistaPublicaApi } from '../api/procesos'
import logoLigier from '../assets/logo_ligier.png'

// Portal público del EMPLEADO — entrevista de relevamiento por token.
// Mobile-first, blanco y negro con logo Ligier (mismo espíritu que el
// portal del cliente). Sin login: el link ES el acceso.

const INK = '#111111'

export default function EntrevistaPublicaPage() {
  const { token } = useParams()
  const [data, setData] = useState(null)
  const [error, setError] = useState('')
  const [texto, setTexto] = useState('')
  const [enviando, setEnviando] = useState(false)
  const bottomRef = useRef(null)
  const inputRef = useRef(null)

  useEffect(() => {
    entrevistaPublicaApi.get(token)
      .then(({ data }) => setData(data))
      .catch(err => setError(err.response?.status === 404
        ? 'Este link no es válido. Pedile uno nuevo a quien te lo mandó.'
        : 'No se pudo cargar la entrevista. Probá de nuevo en un rato.'))
  }, [token])

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [data?.mensajes?.length, enviando])

  const enviar = async (e) => {
    e.preventDefault()
    const msg = texto.trim()
    if (!msg || enviando) return
    setTexto('')
    setEnviando(true)
    // Eco optimista del mensaje propio mientras la IA responde
    setData(d => ({ ...d, mensajes: [...d.mensajes, { role: 'user', texto: msg }] }))
    try {
      const { data: nueva } = await entrevistaPublicaApi.mensaje(token, msg)
      setData(nueva)
    } catch (err) {
      setError(err.response?.data?.message || 'No se pudo enviar. Probá de nuevo.')
      setTimeout(() => setError(''), 4000)
    } finally {
      setEnviando(false)
      inputRef.current?.focus()
    }
  }

  if (error && !data) {
    return (
      <div style={s.page}>
        <header style={s.header}><img src={logoLigier} alt="Ligier" style={s.logo} /></header>
        <p style={s.errorFull}>{error}</p>
      </div>
    )
  }
  if (!data) {
    return (
      <div style={s.page}>
        <header style={s.header}><img src={logoLigier} alt="Ligier" style={s.logo} /></header>
        <p style={s.cargando}>Cargando…</p>
      </div>
    )
  }

  const completada = data.estado === 'completada' || data.estado === 'analizada'

  return (
    <div style={s.page}>
      <header style={s.header}>
        <img src={logoLigier} alt="Ligier" style={s.logo} />
        <div>
          <div style={s.headerTitle}>Charla sobre tu trabajo</div>
          <div style={s.headerSub}>{data.nombre}</div>
        </div>
      </header>

      <main style={s.chat}>
        {data.mensajes.map((m, i) => (
          <div key={i} style={{ ...s.burbuja, ...(m.role === 'user' ? s.burbujaUser : s.burbujaIA) }}>
            {m.texto}
          </div>
        ))}
        {enviando && <div style={{ ...s.burbuja, ...s.burbujaIA, color: '#888' }}>Escribiendo…</div>}
        {error && <div style={s.errorChip}>{error}</div>}
        {completada && (
          <div style={s.fin}>
            ✅ La entrevista quedó completada.<br />¡Muchas gracias por tu tiempo!
          </div>
        )}
        <div ref={bottomRef} />
      </main>

      {!completada && (
        <form onSubmit={enviar} style={s.inputBar}>
          <textarea
            ref={inputRef}
            value={texto}
            onChange={e => setTexto(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) enviar(e) }}
            placeholder="Escribí tu respuesta…"
            rows={1}
            style={s.input}
            disabled={enviando}
          />
          <button type="submit" disabled={enviando || !texto.trim()} style={s.btnEnviar}>➤</button>
        </form>
      )}
    </div>
  )
}

const s = {
  page: {
    minHeight: '100dvh', display: 'flex', flexDirection: 'column',
    background: '#fafafa', maxWidth: 640, margin: '0 auto',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  header: {
    position: 'sticky', top: 0, zIndex: 5, display: 'flex', alignItems: 'center', gap: 12,
    background: '#000', color: '#fff', padding: '12px 16px',
  },
  logo: { height: 28, filter: 'invert(1)' },
  headerTitle: { fontSize: 15, fontWeight: 700 },
  headerSub: { fontSize: 12, opacity: 0.75 },
  chat: { flex: 1, padding: '16px 12px 8px', display: 'flex', flexDirection: 'column', gap: 8 },
  burbuja: {
    maxWidth: '85%', padding: '10px 14px', borderRadius: 16,
    fontSize: 15, lineHeight: 1.45, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
  },
  burbujaIA: { alignSelf: 'flex-start', background: '#fff', border: '1px solid #e5e5e5', color: INK, borderBottomLeftRadius: 4 },
  burbujaUser: { alignSelf: 'flex-end', background: INK, color: '#fff', borderBottomRightRadius: 4 },
  fin: {
    alignSelf: 'center', textAlign: 'center', margin: '16px 0', padding: '14px 20px',
    background: '#dcfce7', color: '#166534', borderRadius: 12, fontSize: 15, fontWeight: 600,
  },
  inputBar: {
    position: 'sticky', bottom: 0, display: 'flex', gap: 8, alignItems: 'flex-end',
    padding: '10px 12px calc(10px + env(safe-area-inset-bottom))', background: '#fafafa',
    borderTop: '1px solid #e5e5e5',
  },
  input: {
    flex: 1, padding: '11px 14px', borderRadius: 22, border: '1px solid #d4d4d4',
    fontSize: 16, fontFamily: 'inherit', resize: 'none', outline: 'none', background: '#fff',
  },
  btnEnviar: {
    width: 44, height: 44, borderRadius: '50%', border: 'none', background: INK,
    color: '#fff', fontSize: 18, cursor: 'pointer', flexShrink: 0,
  },
  cargando: { textAlign: 'center', color: '#888', marginTop: 60 },
  errorFull: { textAlign: 'center', color: '#991b1b', margin: '60px 24px', fontSize: 15 },
  errorChip: { alignSelf: 'center', background: '#fee2e2', color: '#991b1b', padding: '6px 12px', borderRadius: 8, fontSize: 13 },
}
